import type { SldModelRequest } from "../services/sld";
import { BUS_KIND_LABELS } from "./sldDraft";

// Deterministic layered layout of a single-line diagram (SLD-a, Master Prompt A26 (g)).
//
// A pure function over the editor's model: the same model, in any input order, gives the
// same picture. Sources sit on row 0; a bus's row is one below the deepest element that
// feeds it; a load sits one row below its bus. Horizontally the picture is a tidy tree over
// the feeding branches: siblings are sorted by code, a parent is centred over the span of
// its subtree, and a bus fed by several branches hangs under its primary feed - the branch
// with the deepest upstream chain (ties: transformer before source incomer before feeder,
// then code) - while the others route across to it, so a main board is drawn under its
// utility transformer and a generator comes in from the side. Transformers are drawn on
// their edge, not as nodes.
//
// A draft may break the structural rules (the backend refuses it at the save, not here):
// elements in a cycle, not reachable from any source, or attached to a code that does not
// exist are placed on a bottom "unconnected" row so the editor still shows them.
//
// The words are geometry too: every label (a node's, a device's, a cable's, a transformer's)
// is a box estimated from its characters at the label font size, placed beside its symbol
// or its run with a fixed gap, on the first side that crosses no route, no symbol and no
// label placed before it (the cable label of SLD-REF-01 sat on its own run, 30 Sep 2026).
// The diagram draws the words where the layout put them; layout.test.ts and the offline
// gallery hold the boxes apart.
//
// Every number below is a presentation unit (SVG user units), never an engineering value.

/** The presentation constants of the layout, in one place. */
export const SLD_LAYOUT = {
  /** Space between the picture and the edge of the drawing. */
  margin: 40,
  /** Vertical space between the bottom of one row and the top of the next. */
  rowGap: 80,
  /** Horizontal space between sibling subtrees and between unconnected elements. */
  columnGap: 40,
  /** Distance from a bus end to the nearest branch leaving or reaching it. */
  busEndPad: 8,
  source: { width: 120, height: 48 },
  bus: { width: 160, height: 32 },
  load: { width: 96, height: 48 },
  /** The drawn symbols: circles for a source and a load, a square for a device, two
   *  circles for a transformer; a device sits this far along its branch. */
  symbol: {
    sourceRadius: 20,
    loadRadius: 20,
    deviceSize: 12,
    deviceOffset: 18,
    transformerRadius: 10,
  },
  /** The words: line pitch at the label font size (12 px), gap between a symbol and its words. */
  label: { lineHeight: 14, gap: 6 },
} as const;

export type LayoutNodeKind = "source" | "bus" | "load";
export type LayoutEdgeKind = "incomer" | "transformer" | "feeder" | "trunking";

export type LayoutNode = {
  id: string;
  kind: LayoutNodeKind;
  x: number;
  y: number;
  width: number;
  height: number;
};

export type LayoutPoint = [number, number];

export type LayoutBox = { x: number; y: number; width: number; height: number };

export type LayoutEdge = {
  id: string;
  kind: LayoutEdgeKind;
  from: string;
  to: string;
  /** An orthogonal route from the bottom of `from` to the top of `to`. */
  points: LayoutPoint[];
  deviceCode?: string;
  cableCode?: string;
  /** The centre of the device symbol, on the route's first run. */
  devicePoint?: LayoutPoint;
  /** Where the dashed cable part begins, as a distance along the route. */
  cableStart?: number;
  /** The centre of the transformer symbol, on the route's longest vertical run. */
  transformerPoint?: LayoutPoint;
};

export type LayoutLabelKind = "source" | "bus" | "load" | "device" | "cable" | "transformer";

export type LayoutLabel = {
  /** The element the words belong to: a node, device, cable or transformer code. */
  id: string;
  kind: LayoutLabelKind;
  /** The lines of text, top to bottom, never blank. */
  lines: string[];
  /** Left edge of the text (start-anchored) and the vertical centre of the block. */
  x: number;
  y: number;
  /** The box the words take, estimated from the characters at the label font size. */
  box: LayoutBox;
};

export type LayoutSymbol = {
  id: string;
  kind: "device" | "transformer";
  edge: string;
  box: LayoutBox;
};

export type SldLayout = {
  nodes: LayoutNode[];
  edges: LayoutEdge[];
  /** The device and transformer symbols drawn on the edges, as boxes. */
  symbols: LayoutSymbol[];
  /** Every label, placed clear of routes, symbols and one another. */
  labels: LayoutLabel[];
  /** Codes of the nodes on the bottom row and of the branches that could not be drawn. */
  unconnected: string[];
  width: number;
  height: number;
};

type Branch = {
  id: string;
  kind: LayoutEdgeKind;
  from: string;
  to: string;
  deviceCode?: string;
  cableCode?: string;
};

type Size = { readonly width: number; readonly height: number };

const SIZE: Record<LayoutNodeKind, Size> = {
  source: SLD_LAYOUT.source,
  bus: SLD_LAYOUT.bus,
  load: SLD_LAYOUT.load,
};

const ROW_HEIGHT = Math.max(
  SLD_LAYOUT.source.height,
  SLD_LAYOUT.bus.height,
  SLD_LAYOUT.load.height,
);
const ROW_PITCH = ROW_HEIGHT + SLD_LAYOUT.rowGap;

const byCode = (a: string, b: string): number => (a < b ? -1 : a > b ? 1 : 0);

// Among branches whose origins lie equally deep, the kind that decides where a bus sits.
const FEED_RANK: Record<LayoutEdgeKind, number> = {
  transformer: 0,
  incomer: 1,
  feeder: 2,
  trunking: 2,
};

function rowTop(layer: number): number {
  return SLD_LAYOUT.margin + layer * ROW_PITCH;
}

function clamp(value: number, low: number, high: number): number {
  return Math.min(Math.max(value, low), high);
}


// -- routes, symbols and words ----------------------------------------------------------------

export function segmentLength(a: LayoutPoint, b: LayoutPoint): number {
  return Math.abs(b[0] - a[0]) + Math.abs(b[1] - a[1]);
}

/** The point a given distance along an orthogonal polyline, from its start. */
export function pointAlong(points: LayoutPoint[], distance: number): LayoutPoint {
  let remaining = distance;
  for (let index = 1; index < points.length; index += 1) {
    const a = points[index - 1] as LayoutPoint;
    const b = points[index] as LayoutPoint;
    const length = segmentLength(a, b);
    if (remaining <= length || index === points.length - 1) {
      const fraction = length === 0 ? 0 : Math.min(remaining / length, 1);
      return [a[0] + (b[0] - a[0]) * fraction, a[1] + (b[1] - a[1]) * fraction];
    }
    remaining -= length;
  }
  return points[0] ?? [0, 0];
}

export function totalLength(points: LayoutPoint[]): number {
  let total = 0;
  for (let index = 1; index < points.length; index += 1) {
    total += segmentLength(points[index - 1] as LayoutPoint, points[index] as LayoutPoint);
  }
  return total;
}

/** The polyline cut in two at a distance from its start: the first part and the rest. */
export function splitPolyline(
  points: LayoutPoint[],
  distance: number,
): [LayoutPoint[], LayoutPoint[]] {
  const cut = pointAlong(points, distance);
  const head: LayoutPoint[] = [];
  const tail: LayoutPoint[] = [cut];
  let walked = 0;
  let cutDone = false;
  for (let index = 0; index < points.length; index += 1) {
    const point = points[index] as LayoutPoint;
    if (index > 0) {
      walked += segmentLength(points[index - 1] as LayoutPoint, point);
    }
    if (!cutDone && walked >= distance) {
      head.push(cut);
      cutDone = true;
    }
    if (cutDone) {
      tail.push(point);
    } else {
      head.push(point);
    }
  }
  return [head, tail];
}

type Segment = [LayoutPoint, LayoutPoint];

function segmentsOf(points: LayoutPoint[]): Segment[] {
  const segments: Segment[] = [];
  for (let index = 1; index < points.length; index += 1) {
    segments.push([points[index - 1] as LayoutPoint, points[index] as LayoutPoint]);
  }
  return segments;
}

/** The longest vertical run of a route (ties: the later one); null when it has none. */
function longestVerticalRun(points: LayoutPoint[]): Segment | null {
  let best: Segment | null = null;
  for (const segment of segmentsOf(points)) {
    if (
      segment[0][0] === segment[1][0] &&
      segmentLength(...segment) > 0 &&
      (best === null || segmentLength(...segment) >= segmentLength(...best))
    ) {
      best = segment;
    }
  }
  return best;
}

/**
 * Where a transformer symbol sits: the middle of the longest vertical run of the route, or
 * a third of the way down it when the transformer carries its secondary incomer device,
 * which then takes the lower part of the run (transformerDevicePoint).
 */
export function transformerCentre(points: LayoutPoint[], withDevice = false): LayoutPoint {
  const run = longestVerticalRun(points);
  if (run === null) {
    return pointAlong(points, totalLength(points) / 2);
  }
  const [[x, start], [, end]] = run;
  return [x, start + (end - start) * (withDevice ? 1 / 3 : 1 / 2)];
}

/**
 * Where a transformer's secondary incomer device sits: on the transformer's run below the
 * winding (two thirds of the way down, at least a symbol's clearance under the winding),
 * and never past the run's end into the bus. The run goes downstream, so "below" is
 * further along it.
 */
export function transformerDevicePoint(points: LayoutPoint[]): LayoutPoint {
  const { deviceSize, transformerRadius } = SLD_LAYOUT.symbol;
  const gap = SLD_LAYOUT.label.gap;
  const [x, windingY] = transformerCentre(points, true);
  const clearance = transformerRadius * 1.6 + gap + deviceSize / 2;
  const run = longestVerticalRun(points);
  if (run === null) {
    return [x, windingY + clearance];
  }
  const [[, start], [, end]] = run;
  const downstream = end >= start ? 1 : -1;
  const twoThirds = start + ((end - start) * 2) / 3;
  const belowWinding = windingY + downstream * clearance;
  const wanted =
    downstream === 1 ? Math.max(twoThirds, belowWinding) : Math.min(twoThirds, belowWinding);
  const last = end - downstream * (deviceSize / 2 + gap);
  return [x, downstream === 1 ? Math.min(wanted, last) : Math.max(wanted, last)];
}

/** The box a route segment takes, its 2-unit stroke included. */
function segmentBox([a, b]: Segment): LayoutBox {
  const x = Math.min(a[0], b[0]);
  const y = Math.min(a[1], b[1]);
  return {
    x: x - 1,
    y: y - 1,
    width: Math.abs(b[0] - a[0]) + 2,
    height: Math.abs(b[1] - a[1]) + 2,
  };
}

/** The box of the drawn symbol of a node: the bar of a bus, the circle of a source or load. */
export function nodeSymbolBox(node: LayoutNode): LayoutBox {
  if (node.kind === "bus") {
    return { x: node.x, y: node.y, width: node.width, height: node.height };
  }
  const radius =
    node.kind === "source" ? SLD_LAYOUT.symbol.sourceRadius : SLD_LAYOUT.symbol.loadRadius;
  return {
    x: node.x + node.width / 2 - radius,
    y: node.y + node.height / 2 - radius,
    width: radius * 2,
    height: radius * 2,
  };
}

/** Two boxes touch or overlap, a gap of `margin` counted as touching. */
export function boxesIntersect(a: LayoutBox, b: LayoutBox, margin = 0): boolean {
  return (
    a.x < b.x + b.width + margin &&
    b.x < a.x + a.width + margin &&
    a.y < b.y + b.height + margin &&
    b.y < a.y + a.height + margin
  );
}

/** "415 V" for a typed value, "" for a blank or missing one. */
export function withUnit(value: unknown, unit: string): string {
  return typeof value !== "string" || value.trim() === "" ? "" : `${value.trim()} ${unit}`;
}

/** The label of a chosen kind; "" for a choice not made yet or a value this build does not know. */
export function kindLabel(labels: Record<string, string>, kind: unknown): string {
  return typeof kind === "string" && Object.prototype.hasOwnProperty.call(labels, kind)
    ? (labels[kind] ?? "")
    : "";
}

/** "500 kVA" of a generator or a UPS, "50 kW" of a PV source, "" for a source without one. */
export function sourceRatingWords(
  source: { kind?: unknown; rating_kva?: unknown; rating_kw?: unknown } | undefined,
): string {
  if (source === undefined) {
    return "";
  }
  return source.kind === "PV" ? withUnit(source.rating_kw, "kW") : withUnit(source.rating_kva, "kVA");
}

/** "MCCB 400 A", "MCCB", "400 A" or "" - whatever of the device is typed so far. */
export function deviceWords(device: { kind?: unknown; rating_a?: unknown }): string {
  return [typeof device.kind === "string" ? device.kind.trim() : "", withUnit(device.rating_a, "A")]
    .filter(Boolean)
    .join(" ");
}

/**
 * The width of a line at the label font size (12 px IBM Plex Sans, styles/sld.css), from
 * its characters: an estimate on the wide side, so a box that clears here clears in the
 * browser too (the offline gallery measures the rendered boxes).
 */
export function textWidth(line: string): number {
  let width = 2;
  for (const character of line) {
    if (/[ .,:;'|!il]/.test(character)) {
      width += 3.5;
    } else if (/[mw]/.test(character)) {
      width += 9.5;
    } else if (/[A-Z]/.test(character)) {
      width += 8;
    } else if (/[a-z]/.test(character)) {
      width += 6.5;
    } else {
      width += 7;
    }
  }
  return width;
}

/** The box of a label block whose text starts at x and is centred on y. */
function labelBox(x: number, y: number, width: number, lines: number): LayoutBox {
  const pitch = SLD_LAYOUT.label.lineHeight;
  // The first baseline sits (lines - 1) / 2 pitches above the centre; the glyphs reach
  // about 10 units above a baseline and 4 below it.
  return { x, y: y - ((lines - 1) * pitch) / 2 - 10, width, height: lines * pitch };
}

type LabelSide = (width: number, height: number) => { x: number; y: number };

/**
 * The first form and side that cross no obstacle and no label placed before: every side
 * of the first form (the usual words), then every side of the next (the same words stacked
 * narrower), and so on; when nothing is clear, the first side of the first form.
 */
function placeLabel(
  kind: LayoutLabelKind,
  id: string,
  forms: string[][],
  candidates: LabelSide[],
  obstacles: readonly LayoutBox[],
  labels: LayoutLabel[],
): void {
  const gap = SLD_LAYOUT.label.gap;
  type Option = { lines: string[]; at: { x: number; y: number }; box: LayoutBox };
  const options: Option[] = [];
  for (const form of forms) {
    const lines = form.filter((line) => line.trim() !== "");
    if (lines.length === 0) {
      continue;
    }
    const width = Math.max(...lines.map(textWidth));
    const height = lines.length * SLD_LAYOUT.label.lineHeight;
    for (const side of candidates) {
      const at = side(width, height);
      options.push({ lines, at, box: labelBox(at.x, at.y, width, lines.length) });
    }
  }
  if (options.length === 0) {
    return;
  }
  const clear = options.find(
    ({ box }) =>
      box.x >= 2 &&
      box.y >= 2 &&
      !obstacles.some((obstacle) => boxesIntersect(box, obstacle, gap / 2)) &&
      !labels.some((label) => boxesIntersect(box, label.box, gap / 2)),
  );
  const chosen = clear ?? (options[0] as Option);
  labels.push({ id, kind, lines: chosen.lines, x: chosen.at.x, y: chosen.at.y, box: chosen.box });
}

/** Right of a box, then left of it, at its vertical centre. */
function besideBox(box: LayoutBox): LabelSide[] {
  const gap = SLD_LAYOUT.label.gap;
  const cy = box.y + box.height / 2;
  return [
    () => ({ x: box.x + box.width + gap, y: cy }),
    (width) => ({ x: box.x - gap - width, y: cy }),
  ];
}

/** Beside a vertical run: right then left at its middle, then at a quarter and three quarters. */
function besideRun(run: Segment): LabelSide[] {
  const gap = SLD_LAYOUT.label.gap;
  const x = run[0][0];
  const top = Math.min(run[0][1], run[1][1]);
  const length = Math.abs(run[1][1] - run[0][1]);
  const sides: LabelSide[] = [];
  for (const fraction of [0.5, 0.25, 0.75]) {
    const y = top + length * fraction;
    sides.push(() => ({ x: x + gap, y }));
    sides.push((width) => ({ x: x - gap - width, y }));
  }
  return sides;
}

/** Above then below a horizontal run, centred on it. */
function alongRun(run: Segment): LabelSide[] {
  const gap = SLD_LAYOUT.label.gap;
  const y = run[0][1];
  const cx = (run[0][0] + run[1][0]) / 2;
  return [
    (width, height) => ({ x: cx - width / 2, y: y - gap - height / 2 }),
    (width, height) => ({ x: cx - width / 2, y: y + gap + height / 2 }),
  ];
}

/** The nodes of the model by code; a code used twice keeps the first element read. */
function collectNodes(model: SldModelRequest): Map<string, LayoutNodeKind> {
  const kinds = new Map<string, LayoutNodeKind>();
  const add = (code: string, kind: LayoutNodeKind): void => {
    if (!kinds.has(code)) {
      kinds.set(code, kind);
    }
  };
  for (const source of model.sources) add(source.code, "source");
  for (const bus of model.buses) add(bus.code, "bus");
  for (const load of model.loads) add(load.code, "load");
  return kinds;
}

/** Every branch of the model with its endpoints as typed, sorted by code. */
function collectBranches(model: SldModelRequest): Branch[] {
  const branches: Branch[] = [];
  for (const incomer of model.source_incomers) {
    branches.push({
      id: incomer.code,
      kind: "incomer",
      from: incomer.source,
      to: incomer.to_bus,
      ...(incomer.device ? { deviceCode: incomer.device.code } : {}),
    });
  }
  for (const transformer of model.transformers) {
    branches.push({
      id: transformer.code,
      kind: "transformer",
      from: transformer.from_bus,
      to: transformer.to_bus,
      ...(transformer.device ? { deviceCode: transformer.device.code } : {}),
    });
  }
  for (const feeder of model.feeders) {
    branches.push({
      id: feeder.code,
      kind: feeder.kind === "BUSBAR_TRUNKING" ? "trunking" : "feeder",
      from: feeder.from_bus,
      to: feeder.to_bus ?? feeder.to_load ?? "",
      ...(feeder.device ? { deviceCode: feeder.device.code } : {}),
      ...(feeder.cable ? { cableCode: feeder.cable.code } : {}),
    });
  }
  return branches.sort((a, b) => byCode(a.id, b.id));
}

/**
 * The row of every element a source reaches without passing through a cycle: sources on
 * row 0, every other element one below the deepest element feeding it. Elements left out
 * are unreachable, in a cycle, or below one.
 */
function isUsable(kinds: Map<string, LayoutNodeKind>, branch: Branch): boolean {
  return kinds.has(branch.from) && kinds.has(branch.to) && branch.from !== branch.to;
}

function assignLayers(
  kinds: Map<string, LayoutNodeKind>,
  branches: Branch[],
): Map<string, number> {
  const outgoing = new Map<string, Branch[]>();
  const incomingCount = new Map<string, number>();
  for (const branch of branches) {
    if (!isUsable(kinds, branch)) {
      continue;
    }
    const list = outgoing.get(branch.from) ?? [];
    list.push(branch);
    outgoing.set(branch.from, list);
  }

  // Reachable from a source, over the typed branches.
  const reachable = new Set<string>();
  const queue: string[] = [];
  for (const [code, kind] of kinds) {
    if (kind === "source") {
      reachable.add(code);
      queue.push(code);
    }
  }
  while (queue.length > 0) {
    const code = queue.shift() as string;
    for (const branch of outgoing.get(code) ?? []) {
      if (!reachable.has(branch.to)) {
        reachable.add(branch.to);
        queue.push(branch.to);
      }
    }
  }

  // Longest path from the sources in topological order; a cycle never reaches count 0.
  for (const code of reachable) {
    incomingCount.set(code, 0);
  }
  for (const code of reachable) {
    for (const branch of outgoing.get(code) ?? []) {
      incomingCount.set(branch.to, (incomingCount.get(branch.to) ?? 0) + 1);
    }
  }
  const layers = new Map<string, number>();
  const deepestFeed = new Map<string, number>();
  const ready = [...reachable].filter((code) => incomingCount.get(code) === 0).sort(byCode);
  for (const code of ready) {
    layers.set(code, 0);
  }
  while (ready.length > 0) {
    const code = ready.shift() as string;
    const layer = layers.get(code) ?? 0;
    for (const branch of outgoing.get(code) ?? []) {
      deepestFeed.set(branch.to, Math.max(deepestFeed.get(branch.to) ?? 0, layer + 1));
      const remaining = (incomingCount.get(branch.to) ?? 0) - 1;
      incomingCount.set(branch.to, remaining);
      if (remaining === 0) {
        layers.set(branch.to, deepestFeed.get(branch.to) ?? 0);
        ready.push(branch.to);
      }
    }
  }
  return layers;
}

/**
 * Lay the model out.
 *
 * Nodes are the sources, buses and loads; edges the incomers, transformers, feeders and
 * trunkings that join two placed nodes. `unconnected` names what the bottom row holds and
 * the branches that could not be drawn, sorted by code. An empty model has zero size.
 */
export function layoutSld(model: SldModelRequest): SldLayout {
  const kinds = collectNodes(model);
  const branches = collectBranches(model);
  const layers = assignLayers(kinds, branches);

  const drawn = branches.filter(
    (branch) => isUsable(kinds, branch) && layers.has(branch.from) && layers.has(branch.to),
  );
  const unconnected = new Set<string>();
  for (const branch of branches) {
    if (!drawn.includes(branch)) {
      unconnected.add(branch.id);
    }
  }

  // The tree: every placed non-source node hangs under its primary feed, the branch whose
  // origin lies deepest (ties: transformer, then source incomer, then feeder, then code);
  // the other branches into it route across.
  const preferred = (a: Branch, b: Branch): number => {
    const depth = (layers.get(b.from) ?? 0) - (layers.get(a.from) ?? 0);
    if (depth !== 0) {
      return depth;
    }
    const rank = FEED_RANK[a.kind] - FEED_RANK[b.kind];
    return rank !== 0 ? rank : byCode(a.id, b.id);
  };
  const children = new Map<string, string[]>();
  const primaryBranch = new Map<string, Branch>();
  for (const branch of drawn) {
    const current = primaryBranch.get(branch.to);
    if (current === undefined || preferred(branch, current) < 0) {
      primaryBranch.set(branch.to, branch);
    }
  }
  for (const [code, branch] of primaryBranch) {
    const list = children.get(branch.from) ?? [];
    list.push(code);
    children.set(branch.from, list);
  }
  for (const list of children.values()) {
    list.sort(byCode);
  }

  const subtreeWidth = new Map<string, number>();
  const measure = (code: string): number => {
    const known = subtreeWidth.get(code);
    if (known !== undefined) {
      return known;
    }
    const own = SIZE[kinds.get(code) ?? "bus"].width;
    const below = children.get(code) ?? [];
    const span =
      below.reduce((total, child) => total + measure(child), 0) +
      Math.max(below.length - 1, 0) * SLD_LAYOUT.columnGap;
    const width = Math.max(own, span);
    subtreeWidth.set(code, width);
    return width;
  };

  const nodes: LayoutNode[] = [];
  const placed = new Map<string, LayoutNode>();
  const place = (code: string, left: number): void => {
    const kind = kinds.get(code) ?? "bus";
    const size = SIZE[kind];
    const width = measure(code);
    const node: LayoutNode = {
      id: code,
      kind,
      x: left + (width - size.width) / 2,
      y: rowTop(layers.get(code) ?? 0),
      width: size.width,
      height: size.height,
    };
    nodes.push(node);
    placed.set(code, node);
    const below = children.get(code) ?? [];
    const span =
      below.reduce((total, child) => total + measure(child), 0) +
      Math.max(below.length - 1, 0) * SLD_LAYOUT.columnGap;
    let cursor = left + (width - span) / 2;
    for (const child of below) {
      place(child, cursor);
      cursor += measure(child) + SLD_LAYOUT.columnGap;
    }
  };

  const roots = [...kinds.keys()].filter((code) => layers.get(code) === 0).sort(byCode);
  let cursor = SLD_LAYOUT.margin;
  for (const root of roots) {
    place(root, cursor);
    cursor += measure(root) + SLD_LAYOUT.columnGap;
  }

  // The bottom row: every node no source reaches in a straight line, sorted by code.
  const stranded = [...kinds.keys()].filter((code) => !layers.has(code)).sort(byCode);
  if (stranded.length > 0) {
    const bottomLayer = layers.size > 0 ? Math.max(...layers.values()) + 1 : 0;
    let left = SLD_LAYOUT.margin;
    for (const code of stranded) {
      const kind = kinds.get(code) ?? "bus";
      const node: LayoutNode = {
        id: code,
        kind,
        x: left,
        y: rowTop(bottomLayer),
        width: SIZE[kind].width,
        height: SIZE[kind].height,
      };
      nodes.push(node);
      placed.set(code, node);
      unconnected.add(code);
      left += node.width + SLD_LAYOUT.columnGap;
    }
  }

  // Routes: down from the feeding node, across in the gap above the fed node, down into
  // it. A branch leaves a bus at the point nearest its target and reaches a bus at the
  // point nearest its origin, so several branches share a bus without lying on each other;
  // a source and a load are joined at their centre.
  const edges: LayoutEdge[] = drawn.map((branch) => {
    const from = placed.get(branch.from) as LayoutNode;
    const to = placed.get(branch.to) as LayoutNode;
    const fromCentre = from.x + from.width / 2;
    const toCentre = to.x + to.width / 2;
    const pad = SLD_LAYOUT.busEndPad;
    const startX =
      from.kind === "bus" ? clamp(toCentre, from.x + pad, from.x + from.width - pad) : fromCentre;
    const endX =
      to.kind === "bus" ? clamp(fromCentre, to.x + pad, to.x + to.width - pad) : toCentre;
    const startY = from.y + from.height;
    const endY = to.y;
    const points: LayoutPoint[] =
      startX === endX
        ? [
            [startX, startY],
            [endX, endY],
          ]
        : [
            [startX, startY],
            [startX, endY - SLD_LAYOUT.rowGap / 2],
            [endX, endY - SLD_LAYOUT.rowGap / 2],
            [endX, endY],
          ];
    // The symbols on the route: the device a fixed way along the first run, the cable's
    // dashed part after it, the transformer at the middle of the longest vertical run.
    const { deviceSize, deviceOffset } = SLD_LAYOUT.symbol;
    const length = totalLength(points);
    // A transformer's device is its secondary incomer: below the winding, not at the start.
    const devicePoint =
      branch.deviceCode === undefined
        ? null
        : branch.kind === "transformer"
          ? transformerDevicePoint(points)
          : pointAlong(points, deviceOffset);
    const cableStart =
      branch.cableCode !== undefined
        ? Math.min(deviceOffset + deviceSize + SLD_LAYOUT.label.gap * 2, length / 2)
        : null;
    const transformerPoint =
      branch.kind === "transformer"
        ? transformerCentre(points, branch.deviceCode !== undefined)
        : null;
    return {
      id: branch.id,
      kind: branch.kind,
      from: branch.from,
      to: branch.to,
      points,
      ...(branch.deviceCode !== undefined ? { deviceCode: branch.deviceCode } : {}),
      ...(branch.cableCode !== undefined ? { cableCode: branch.cableCode } : {}),
      ...(devicePoint !== null ? { devicePoint } : {}),
      ...(cableStart !== null ? { cableStart } : {}),
      ...(transformerPoint !== null ? { transformerPoint } : {}),
    };
  });

  nodes.sort((a, b) => byCode(a.id, b.id));

  // The symbols on the edges as boxes, obstacles for the words.
  const symbols: LayoutSymbol[] = [];
  for (const edge of edges) {
    if (edge.devicePoint !== undefined && edge.deviceCode !== undefined) {
      const half = SLD_LAYOUT.symbol.deviceSize / 2;
      symbols.push({
        id: edge.deviceCode,
        kind: "device",
        edge: edge.id,
        box: {
          x: edge.devicePoint[0] - half,
          y: edge.devicePoint[1] - half,
          width: half * 2,
          height: half * 2,
        },
      });
    }
    if (edge.transformerPoint !== undefined) {
      const radius = SLD_LAYOUT.symbol.transformerRadius;
      symbols.push({
        id: edge.id,
        kind: "transformer",
        edge: edge.id,
        box: {
          x: edge.transformerPoint[0] - radius,
          y: edge.transformerPoint[1] - radius * 1.6,
          width: radius * 2,
          height: radius * 3.2,
        },
      });
    }
  }

  const labels = placeLabels(model, nodes, edges, symbols);

  const rights = [
    ...nodes.map((node) => node.x + node.width),
    ...labels.map((label) => label.box.x + label.box.width),
  ];
  const bottoms = [
    ...nodes.map((node) => node.y + node.height),
    ...labels.map((label) => label.box.y + label.box.height),
  ];
  const width = nodes.length > 0 ? Math.max(...rights) + SLD_LAYOUT.margin : 0;
  const height = nodes.length > 0 ? Math.max(...bottoms) + SLD_LAYOUT.margin : 0;

  return {
    nodes,
    edges,
    symbols,
    labels,
    unconnected: [...unconnected].sort(byCode),
    width,
    height,
  };
}

/**
 * Every label beside its symbol or its run, clear of every route, symbol and label placed
 * before it: the nodes' words first (sorted by code), then per edge (sorted by id) the
 * transformer's, the device's and the cable's. A label with no clear side takes its first
 * side; the guards in layout.test.ts say when that happens.
 */
function placeLabels(
  model: SldModelRequest,
  nodes: LayoutNode[],
  edges: LayoutEdge[],
  symbols: LayoutSymbol[],
): LayoutLabel[] {
  const first = <T extends { code: string }>(rows: readonly T[], code: string): T | undefined =>
    rows.find((row) => row.code === code);
  const obstacles: LayoutBox[] = [
    ...nodes.map(nodeSymbolBox),
    ...edges.flatMap((edge) => segmentsOf(edge.points).map(segmentBox)),
    ...symbols.map((symbol) => symbol.box),
  ];
  const labels: LayoutLabel[] = [];

  for (const node of nodes) {
    const box = nodeSymbolBox(node);
    if (node.kind === "source") {
      const source = first(model.sources, node.id);
      placeLabel(
        "source",
        node.id,
        [
          [
            node.id,
            [withUnit(source?.voltage_v, "V"), sourceRatingWords(source)]
              .filter(Boolean)
              .join(" · "),
          ],
        ],
        besideBox(box),
        obstacles,
        labels,
      );
    } else if (node.kind === "bus") {
      const bus = first(model.buses, node.id);
      placeLabel(
        "bus",
        node.id,
        [[withUnit(bus?.nominal_voltage_v, "V"), kindLabel(BUS_KIND_LABELS, bus?.kind)]],
        besideBox(box),
        obstacles,
        labels,
      );
    } else {
      const load = first(model.loads, node.id);
      placeLabel(
        "load",
        node.id,
        [[node.id, withUnit(load?.rated_kw, "kW")]],
        besideBox(box),
        obstacles,
        labels,
      );
    }
  }

  for (const edge of edges) {
    const symbolsOf = symbols.filter((symbol) => symbol.edge === edge.id);
    const transformerSymbol = symbolsOf.find((symbol) => symbol.kind === "transformer");
    if (transformerSymbol !== undefined) {
      const transformer = first(model.transformers, edge.id);
      placeLabel(
        "transformer",
        edge.id,
        [[edge.id, withUnit(transformer?.rating_kva, "kVA")]],
        besideBox(transformerSymbol.box),
        obstacles,
        labels,
      );
    }
    const deviceSymbol = symbolsOf.find((symbol) => symbol.kind === "device");
    if (deviceSymbol !== undefined) {
      const device =
        first(model.feeders, edge.id)?.device ??
        first(model.source_incomers, edge.id)?.device ??
        first(model.transformers, edge.id)?.device;
      // A device sits close under its bus, so its words hang from the device's row (the
      // code on the device's line, the rating below) before they are centred on it.
      const pitch = SLD_LAYOUT.label.lineHeight;
      const hanging = (sides: LabelSide[]): LabelSide[] =>
        sides.map((side) => (width, height) => {
          const at = side(width, height);
          return { x: at.x, y: at.y + (height - pitch) / 2 };
        });
      const shifted = (dy: number): LabelSide[] =>
        besideBox({ ...deviceSymbol.box, y: deviceSymbol.box.y + dy });
      // Between close siblings the kind and the rating go on separate lines (narrower).
      const kindWord = typeof device?.kind === "string" ? device.kind.trim() : "";
      const rating = withUnit(device?.rating_a, "A");
      placeLabel(
        "device",
        deviceSymbol.id,
        [
          [deviceSymbol.id, device ? deviceWords(device) : ""],
          [deviceSymbol.id, kindWord, rating],
        ],
        [
          ...hanging(besideBox(deviceSymbol.box)),
          ...besideBox(deviceSymbol.box),
          ...hanging(shifted(pitch)),
          ...hanging(shifted(pitch * 2)),
        ],
        obstacles,
        labels,
      );
    }
    if (edge.cableCode !== undefined && edge.cableStart !== undefined) {
      const cable = first(model.feeders, edge.id)?.cable;
      const [, dashed] = splitPolyline(edge.points, edge.cableStart);
      const run = longestVerticalRun(dashed);
      const horizontal = segmentsOf(dashed).find((segment) => segment[0][1] === segment[1][1]);
      const sides =
        run !== null ? besideRun(run) : horizontal !== undefined ? alongRun(horizontal) : [];
      placeLabel(
        "cable",
        edge.cableCode,
        [[edge.cableCode, withUnit(cable?.length_m, "m")]],
        sides,
        obstacles,
        labels,
      );
    }
  }
  return labels;
}
